import {
  ArrowLeft,
  Columns,
  Copy,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  UserMinus,
  UserPlus,
  UsersThree,
  WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useToast } from '@/hooks/use-toast';
import { ContactsPagination } from '@/modules/contacts/components/contacts-pagination/contacts-pagination';
import type { Contact } from '@/modules/contacts/types/contacts.types';
import { getContactDisplayName } from '@/modules/contacts/utils/contact-attributes';
import { formatContactSourceLabel } from '@/modules/contacts/utils/contact-source.utils';
import { AddListMembersModal } from '../components/add-list-members-modal';
import {
  ListMembersFiltersDropdown,
  type ListMembersFilters,
} from '../components/list-members-filters-dropdown';
import { ListFormModal } from '../components/list-form-modal';
import {
  useGetListById,
  useGetListContacts,
  useDeleteList,
  useRemoveListMembers,
} from '../hooks/use-lists';
import { formatListCreatedDate, formatMemberCountLabel } from '../utils/list-api-mapper';
import { isListNotFoundError } from '../utils/list-errors';

const tableHeadClassName =
  'sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600';

const OPTIONAL_COLUMNS = [
  { id: 'phone', label: 'Phone' },
  { id: 'source', label: 'Source' },
  { id: 'created', label: 'Created' },
] as const;

type OptionalColumnId = (typeof OPTIONAL_COLUMNS)[number]['id'];

const COLUMN_STORAGE_KEY = 'list-members-table-columns';

const loadColumnVisibility = (): Record<OptionalColumnId, boolean> => {
  const defaults = { phone: true, source: true, created: true };
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
  } catch {
    return defaults;
  }
};

const formatMemberCreatedDate = (value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const ListDetailsPage = () => {
  const { listId = '' } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const decodedListId = decodeURIComponent(listId);

  const {
    data: list,
    isLoading: isListLoading,
    error: listError,
    refetch: refetchList,
  } = useGetListById(decodedListId);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<ListMembersFilters>({});
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<Contact | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkRemoveOpen, setIsBulkRemoveOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] =
    useState<Record<OptionalColumnId, boolean>>(loadColumnVisibility);

  const toggleColumn = (id: OptionalColumnId, visible: boolean) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: visible };
      try {
        window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Persistence is best-effort.
      }
      return next;
    });
  };

  const { mutateAsync: removeMembers } = useRemoveListMembers();
  const { mutateAsync: deleteList, isPending: isDeletingList } = useDeleteList();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // A new narrowing starts from the first page.
  useEffect(() => {
    setPageNo(1);
  }, [debouncedSearch, pageSize, filters]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [pageNo, pageSize, debouncedSearch, decodedListId]);

  const {
    data: membersData,
    isLoading: isMembersLoading,
    error: membersError,
    refetch: refetchMembers,
  } = useGetListContacts({
    listId: decodedListId,
    pageNo,
    pageSize,
    q: debouncedSearch || undefined,
    filter: filters,
  });

  const members = membersData?.items ?? [];
  const totalCount = membersData?.totalCount ?? list?.memberCount ?? 0;
  const notFound = Boolean(listError) && isListNotFoundError(listError);

  const handleConfirmRemove = async () => {
    if (!memberToRemove?.ItemId) return;

    try {
      await removeMembers({ listId: decodedListId, contactIds: [memberToRemove.ItemId] });
      toast({
        variant: 'success',
        title: `${getContactDisplayName(memberToRemove) || 'Contact'} removed from ${list?.name ?? 'this list'}.`,
      });
      setMemberToRemove(null);
    } catch (removeError) {
      toast({
        variant: 'destructive',
        title: 'Failed to remove member',
        description: removeError instanceof Error ? removeError.message : 'Please try again.',
      });
    }
  };

  const handleConfirmBulkRemove = async () => {
    const contactIds = [...selectedIds];
    if (contactIds.length === 0) return;

    try {
      await removeMembers({ listId: decodedListId, contactIds });
      toast({
        variant: 'success',
        title: `${contactIds.length} member${contactIds.length === 1 ? '' : 's'} removed from ${list?.name ?? 'this list'}.`,
      });
      setSelectedIds(new Set());
      setIsBulkRemoveOpen(false);
    } catch (removeError) {
      toast({
        variant: 'destructive',
        title: 'Failed to remove members',
        description: removeError instanceof Error ? removeError.message : 'Please try again.',
      });
    }
  };

  const handleConfirmDeleteList = async () => {
    try {
      await deleteList(decodedListId);
      toast({
        variant: 'success',
        title: 'List deleted',
        description: `${list?.name ?? 'The list'} was deleted.`,
      });
      setIsDeleteConfirmOpen(false);
      navigate('/lists');
    } catch (deleteError) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete list',
        description: deleteError instanceof Error ? deleteError.message : 'Please try again.',
      });
    }
  };

  if (isListLoading && !list) {
    return (
      <div className="flex h-full min-h-0 w-full max-w-[1400px] flex-col gap-5">
        <Skeleton className="h-4 w-48" />
        <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="mt-2 h-4 w-80" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border border-neutral-200 bg-white py-16 shadow-e1">
        <WarningCircle className="size-7 text-error" aria-hidden />
        <p className="font-medium text-neutral-900">List not found</p>
        <p className="max-w-[360px] text-center text-[13px] text-neutral-500">
          It may have been renamed, or the link is out of date.
        </p>
        <Button
          type="button"
          className="mt-1 bg-primary-600 hover:bg-primary-700"
          onClick={() => navigate('/lists')}
        >
          Back to Lists
        </Button>
      </div>
    );
  }

  if (listError && !list) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border border-neutral-200 bg-white py-16 shadow-e1">
        <WarningCircle className="size-7 text-error" aria-hidden />
        <p className="font-medium text-neutral-900">Could not load this list</p>
        <p className="max-w-[360px] text-center text-[13px] text-neutral-500">
          Something went wrong on our end. Try again in a moment.
        </p>
        <Button
          type="button"
          className="mt-1 bg-primary-600 hover:bg-primary-700"
          onClick={() => void refetchList()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!list) return null;

  const memberCount = membersData?.totalCount ?? list.memberCount;
  const showMembersLoading = isMembersLoading && members.length === 0;
  const allPageSelected =
    members.length > 0 && members.every((member) => selectedIds.has(member.ItemId));
  const columnCount =
    3 + OPTIONAL_COLUMNS.filter((column) => columnVisibility[column.id]).length;

  return (
    <div className="flex h-full min-h-0 w-full max-w-[1400px] flex-col gap-5">
      <Link
        to="/lists"
        className="inline-flex w-fit items-center gap-1.5 text-[13px] font-medium text-neutral-600 hover:text-neutral-900"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to Lists
      </Link>

      <div className="flex flex-wrap items-start gap-4 rounded-lg border border-neutral-200 bg-white px-5 py-5 shadow-e1">
        <div className="min-w-[240px] flex-1">
          <h1 className="text-[28px] font-semibold leading-9 text-neutral-900">{list.name}</h1>
          <p className="mt-1 text-sm text-neutral-600">
            {list.description || <span className="text-neutral-400">No description</span>}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-[12px] font-medium text-neutral-800 hover:bg-neutral-200"
              title={`List ID: ${list.id} — click to copy`}
              aria-label="Copy list ID"
              onClick={() => {
                navigator.clipboard
                  .writeText(list.id)
                  .then(() => toast({ variant: 'success', title: 'List ID copied' }))
                  .catch(() => toast({ variant: 'destructive', title: 'Could not copy list ID' }));
              }}
            >
              <Copy className="size-3.5" aria-hidden />
              {list.id.length > 12 ? `${list.id.slice(0, 6)}…${list.id.slice(-4)}` : list.id}
            </button>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-[12px] font-medium text-neutral-800">
              <UsersThree className="size-3.5" aria-hidden />
              {formatMemberCountLabel(memberCount)}
            </span>
            <span className="text-[13px] text-neutral-500">
              Created {formatListCreatedDate(list.createdAt)}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            className="h-10 text-error hover:bg-error-background/40 hover:text-error"
            onClick={() => setIsDeleteConfirmOpen(true)}
          >
            <Trash className="size-4" aria-hidden />
            Delete list
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-10 border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-100"
            onClick={() => setEditOpen(true)}
          >
            <PencilSimple className="size-4" aria-hidden />
            Edit list
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold text-neutral-900">Members</h2>
        <div className="flex-1" />
        <Button
          type="button"
          className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
          onClick={() => setAddOpen(true)}
        >
          <UserPlus className="size-4" aria-hidden />
          Add members
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-500 focus-within:border-primary-600 focus-within:ring-2 focus-within:ring-primary-600/30">
          <MagnifyingGlass className="size-4 shrink-0" aria-hidden />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search members by name, email, or phone"
            className="h-auto border-0 p-0 shadow-none focus-visible:ring-0"
            aria-label="Search members by name, email, or phone"
          />
        </div>
        <ListMembersFiltersDropdown filters={filters} onApply={setFilters} />
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="h-10 shrink-0 gap-2 px-4 text-sm">
              <Columns className="size-4" aria-hidden />
              Columns
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 space-y-3 p-4">
            <p className="text-xs text-neutral-500">Name and Email are always shown.</p>
            {OPTIONAL_COLUMNS.map((column) => (
              <label key={column.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={columnVisibility[column.id]}
                  onCheckedChange={(checked) => toggleColumn(column.id, checked === true)}
                />
                {column.label}
              </label>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      {selectedIds.size > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5">
          <span className="text-sm font-medium text-neutral-900">{selectedIds.size} selected</span>
          <Button
            type="button"
            variant="outline"
            className="h-8 gap-1.5 border-neutral-200 bg-white px-3 text-[13px] text-error hover:text-error"
            onClick={() => setIsBulkRemoveOpen(true)}
          >
            <UserMinus className="size-4" aria-hidden />
            Remove from list
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-8 px-3 text-[13px]"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </Button>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm leading-5">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className={`${tableHeadClassName} w-10`}>
                  <Checkbox
                    aria-label="Select all members on this page"
                    checked={
                      allPageSelected ? true : selectedIds.size > 0 ? 'indeterminate' : false
                    }
                    disabled={members.length === 0}
                    onCheckedChange={(checked) =>
                      setSelectedIds(
                        checked === true
                          ? new Set(members.map((member) => member.ItemId).filter(Boolean))
                          : new Set()
                      )
                    }
                  />
                </th>
                <th className={tableHeadClassName}>Name</th>
                <th className={tableHeadClassName}>Email</th>
                {columnVisibility.phone ? <th className={tableHeadClassName}>Phone</th> : null}
                {columnVisibility.source ? <th className={tableHeadClassName}>Source</th> : null}
                {columnVisibility.created ? <th className={tableHeadClassName}>Created</th> : null}
                <th className={`${tableHeadClassName} w-14`} />
              </tr>
            </thead>
            <tbody>
              {showMembersLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`member-skeleton-${index}`} className="border-b border-neutral-100">
                      <td className="px-4 py-3">
                        <span className="block h-4 w-32 animate-pulse rounded bg-neutral-200" />
                      </td>
                      <td className="px-4 py-3">
                        <span className="block h-4 w-40 animate-pulse rounded bg-neutral-200" />
                      </td>
                      <td className="px-4 py-3">
                        <span className="block h-4 w-24 animate-pulse rounded bg-neutral-200" />
                      </td>
                      <td className="px-4 py-3">
                        <span className="block h-4 w-28 animate-pulse rounded bg-neutral-200" />
                      </td>
                      <td className="px-4 py-3">
                        <span className="block h-4 w-20 animate-pulse rounded bg-neutral-200" />
                      </td>
                      <td className="px-4 py-3" />
                    </tr>
                  ))
                : null}

              {!showMembersLoading && membersError ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-10 text-center">
                    <WarningCircle className="mx-auto size-7 text-error" aria-hidden />
                    <p className="mt-2 font-medium text-neutral-900">Could not load members</p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-3 h-8"
                      onClick={() => void refetchMembers()}
                    >
                      Retry
                    </Button>
                  </td>
                </tr>
              ) : null}

              {!showMembersLoading && !membersError && members.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-10 text-center">
                    <UsersThree className="mx-auto size-7 text-neutral-400" aria-hidden />
                    <p className="mt-2 font-medium text-neutral-900">No members yet</p>
                    <p className="mt-1 text-[13px] text-neutral-500">
                      {debouncedSearch
                        ? 'No members match your search.'
                        : 'Add contacts to start using this list for targeting.'}
                    </p>
                  </td>
                </tr>
              ) : null}

              {!showMembersLoading &&
                !membersError &&
                members.map((contact) => {
                  const displayName = getContactDisplayName(contact) || '—';
                  return (
                    <tr
                      key={contact.ItemId}
                      className="cursor-pointer border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
                      onClick={() => navigate(`/contacts/${encodeURIComponent(contact.ItemId)}`)}
                    >
                      <td
                        className="px-4 py-3 align-middle"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Checkbox
                          aria-label={`Select ${displayName}`}
                          checked={selectedIds.has(contact.ItemId)}
                          onCheckedChange={(checked) =>
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (checked === true) next.add(contact.ItemId);
                              else next.delete(contact.ItemId);
                              return next;
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 align-middle font-medium text-neutral-900">
                        {displayName}
                      </td>
                      <td className="px-4 py-3 align-middle text-neutral-600">
                        {contact.Email || '—'}
                      </td>
                      {columnVisibility.phone ? (
                        <td className="px-4 py-3 align-middle text-neutral-600">
                          {contact.Phone || '—'}
                        </td>
                      ) : null}
                      {columnVisibility.source ? (
                        <td className="px-4 py-3 align-middle text-neutral-600">
                          {formatContactSourceLabel(contact) || '—'}
                        </td>
                      ) : null}
                      {columnVisibility.created ? (
                        <td className="whitespace-nowrap px-4 py-3 align-middle text-neutral-600">
                          {formatMemberCreatedDate(contact.CreatedDate)}
                        </td>
                      ) : null}
                      <td
                        className="px-4 py-3 align-middle text-right"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="inline-flex size-8 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100 hover:text-error"
                          title="Remove from list"
                          aria-label={`Remove ${displayName} from list`}
                          onClick={() => setMemberToRemove(contact)}
                        >
                          <UserMinus className="size-4" aria-hidden />
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <ContactsPagination
          variant="simple"
          pageNo={membersData?.pageNo ?? pageNo}
          pageSize={membersData?.pageSize ?? pageSize}
          totalCount={totalCount}
          totalPages={membersData?.totalPages ?? 0}
          // The members table has the same footer in the prototype (list-detail.html), where the
          // baseline is the list's own member count rather than the org's contact count — which is
          // exactly what GetListMembersQueryHandler counts for TotalUnfiltered.
          totalUnfiltered={membersData?.totalUnfiltered}
          hasNextPage={Boolean(membersData?.hasNextPage)}
          hasPreviousPage={Boolean(membersData?.hasPreviousPage)}
          onPageChange={setPageNo}
          onPageSizeChange={setPageSize}
          itemLabel="members"
          isLoading={isMembersLoading}
          isError={Boolean(membersError)}
        />
      </div>

      <ListFormModal open={editOpen} onOpenChange={setEditOpen} editingList={list} />

      <AddListMembersModal
        open={addOpen}
        onOpenChange={setAddOpen}
        listId={decodedListId}
        listName={list.name}
        existingMemberIds={members.map((member) => member.ItemId).filter(Boolean)}
      />

      <ConfirmationModal
        open={Boolean(memberToRemove)}
        onOpenChange={(open) => !open && setMemberToRemove(null)}
        title={`Remove ${memberToRemove ? getContactDisplayName(memberToRemove) || 'this contact' : ''} from this list?`}
        description={`This only removes them from ${list.name} — it does not delete the contact or affect their other lists.`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmClassName="bg-destructive hover:bg-destructive/90 text-white"
        preventAutoClose
        onConfirm={() => void handleConfirmRemove()}
      />

      <ConfirmationModal
        open={isBulkRemoveOpen}
        onOpenChange={(open) => setIsBulkRemoveOpen(open)}
        title={`Remove ${selectedIds.size} member${selectedIds.size === 1 ? '' : 's'} from this list?`}
        description={`This only removes them from ${list.name} — it does not delete the contacts or affect their other lists.`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmClassName="bg-destructive hover:bg-destructive/90 text-white"
        preventAutoClose
        onConfirm={() => void handleConfirmBulkRemove()}
      />

      <ConfirmationModal
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (isDeletingList) return;
          setIsDeleteConfirmOpen(open);
        }}
        title={`Delete ${list.name}?`}
        description="This removes the list and its memberships. Contacts themselves are not deleted. This can't be undone."
        confirmText={isDeletingList ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        confirmClassName="bg-destructive hover:bg-destructive/90 text-white"
        preventAutoClose
        onConfirm={() => void handleConfirmDeleteList()}
      />
    </div>
  );
};
